import { useState, useEffect, useRef } from 'react';

interface AudioPreviewCardProps {
  selectedAudio: string;
  onSelectAudio: (audio: string) => void;
  darkMode: boolean;
}

const AUDIO_OPTIONS = [
  {
    id: 'Background Music',
    label: 'Background Music',
    icon: 'ri-music-2-line',
    description: 'Cinematic orchestral score that adapts to your video\'s mood and pacing',
    genre: 'Orchestral / Epic',
    bpm: '96 BPM',
    accentColor: 'from-red-500 to-orange-500',
    barCount: 28,
    barStyle: 'smooth',
    sampleTag: 'Epic Rise',
  },
  {
    id: 'Voiceover',
    label: 'Voiceover',
    icon: 'ri-mic-line',
    description: 'AI-generated professional narrator reads your script in a natural human voice',
    genre: 'Neural TTS',
    bpm: 'Natural Pace',
    accentColor: 'from-amber-500 to-yellow-500',
    barCount: 22,
    barStyle: 'speech',
    sampleTag: 'Pro Voice',
  },
  {
    id: 'Sound Effects',
    label: 'Sound Effects',
    icon: 'ri-sound-module-line',
    description: 'Layered ambient FX and foley sounds synced to on-screen actions',
    genre: 'Foley / Ambient',
    bpm: 'Scene-synced',
    accentColor: 'from-emerald-500 to-teal-500',
    barCount: 32,
    barStyle: 'burst',
    sampleTag: 'Immersive',
  },
];

const WAVEFORM_HEIGHTS: Record<string, number[]> = {
  smooth: [3,5,8,12,16,20,24,28,30,32,28,24,22,26,30,32,28,24,18,14,10,8,6,4,3,5,7,4],
  speech: [4,8,14,20,22,18,10,6,4,12,20,24,20,14,8,4,6,16,22,20,14,8,4,4],
  burst: [4,6,14,8,22,10,28,14,32,10,22,16,30,8,18,24,12,28,8,20,14,26,6,18,30,10,22,16,28,8,20,6],
};

function AnimatedWaveform({ style, accentColor, isPlaying }: { style: string; accentColor: string; isPlaying: boolean }) {
  const heights = WAVEFORM_HEIGHTS[style];
  return (
    <div className="flex items-center gap-[2px] h-10">
      {heights.map((h, i) => (
        <div
          key={i}
          className={`rounded-full bg-gradient-to-t ${accentColor} flex-shrink-0 transition-all`}
          style={{
            width: '3px',
            height: isPlaying ? `${Math.max(4, h * (0.7 + 0.3 * Math.sin((Date.now() / 200 + i * 0.5))))}px` : `${Math.max(4, h * 0.4)}px`,
            animationDelay: `${i * 40}ms`,
            animation: isPlaying ? `waveBar 0.8s ease-in-out ${(i * 40) % 800}ms infinite alternate` : 'none',
            opacity: isPlaying ? 1 : 0.4,
          }}
        />
      ))}
    </div>
  );
}

export default function AudioPreviewCard({ selectedAudio, onSelectAudio, darkMode }: AudioPreviewCardProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [tick, setTick] = useState(0);
  const progressRef = useRef<Record<string, number>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60);
    return () => clearInterval(t);
  }, []);

  const handlePlay = (id: string) => {
    if (playingId === id) {
      clearInterval(timersRef.current[id]);
      setPlayingId(null);
      return;
    }
    if (playingId) clearInterval(timersRef.current[playingId]);

    setPlayingId(id);
    progressRef.current[id] = progressRef.current[id] ?? 0;

    timersRef.current[id] = setInterval(() => {
      progressRef.current[id] = (progressRef.current[id] ?? 0) + 0.8;
      setProgress({ ...progressRef.current });
      if ((progressRef.current[id] ?? 0) >= 100) {
        progressRef.current[id] = 0;
        clearInterval(timersRef.current[id]);
        setPlayingId(null);
      }
    }, 80);
  };

  useEffect(() => {
    return () => { Object.values(timersRef.current).forEach(clearInterval); };
  }, []);

  return (
    <div className={`rounded-2xl border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <style>{`
        @keyframes waveBar {
          from { transform: scaleY(0.5); }
          to   { transform: scaleY(1); }
        }
      `}</style>

      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <h3 className={`text-sm font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <span className="w-5 h-5 flex items-center justify-center text-red-500">
            <i className="ri-headphone-line text-base"></i>
          </span>
          Audio Preview
        </h3>
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
          Preview samples
        </span>
      </div>

      <div className="px-5 pb-5 space-y-3">
        {AUDIO_OPTIONS.map((opt) => {
          const isSelected = selectedAudio === opt.id;
          const isPlaying = playingId === opt.id;
          const pct = progress[opt.id] ?? 0;

          return (
            <div
              key={opt.id}
              onClick={() => onSelectAudio(opt.id)}
              className={`relative rounded-xl p-4 border-2 cursor-pointer transition-all duration-200 ${
                isSelected
                  ? darkMode
                    ? 'border-red-500 bg-gray-900/60'
                    : 'border-red-500 bg-red-50'
                  : darkMode
                  ? 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              {/* Top row */}
              <div className="flex items-center gap-3 mb-3">
                {/* Play button */}
                <button
                  onClick={(e) => { e.stopPropagation(); handlePlay(opt.id); }}
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    isPlaying
                      ? `bg-gradient-to-br ${opt.accentColor} text-white shadow-md`
                      : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-sm ml-${isPlaying ? '0' : '0.5'}`}></i>
                </button>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold truncate ${isSelected ? (darkMode ? 'text-red-400' : 'text-red-600') : (darkMode ? 'text-white' : 'text-gray-900')}`}>
                      {opt.label}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap bg-gradient-to-r ${opt.accentColor} text-white`}>
                      {opt.sampleTag}
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 mt-0.5 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span>{opt.genre}</span>
                    <span>·</span>
                    <span>{opt.bpm}</span>
                  </div>
                </div>

                {/* Selected indicator */}
                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>
                  </div>
                )}
              </div>

              {/* Waveform */}
              <div className="mb-3 overflow-hidden">
                <AnimatedWaveform style={opt.barStyle} accentColor={opt.accentColor} isPlaying={isPlaying} key={tick} />
              </div>

              {/* Progress bar (only when playing) */}
              {isPlaying && (
                <div className="mb-3">
                  <div className={`h-1 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${opt.accentColor} transition-all duration-100`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className={`flex justify-between text-[10px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span>{Math.round(pct * 0.08)}s</span>
                    <span>8s sample</span>
                  </div>
                </div>
              )}

              {/* Description */}
              <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{opt.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
